import { siteConfig } from "./siteConfig";

const LEGAL_ENTITY = "Gaurab Subedi, a sole proprietor doing business as Subedi Tax Pro";

export type LegalSection = { heading: string; body: string[] };
export type LegalPageKey = "privacy" | "terms";

export const legalPages: Record<LegalPageKey, { title: string; updated: string; sections: LegalSection[] }> = {
  privacy: {
    title: "Privacy Policy",
    updated: "August 8, 2026",
    sections: [
      {
        heading: "Who we are",
        body: [
          `This site and client portal are operated by ${LEGAL_ENTITY}, based in Sacramento County, California. Questions about this policy can be sent to ${siteConfig.contactEmail} or ${siteConfig.contactPhone}.`,
        ],
      },
      {
        heading: "Information we collect",
        body: [
          "We collect the information you give us in order to prepare your tax return: your name, email address, mobile phone number, mailing address, and the contents of the tax documents and answers you provide.",
          "We also record limited technical information when you sign in, including your IP address and browser user agent, so that we can secure your account and keep an audit record of access.",
        ],
      },
      {
        heading: "How we use your information",
        body: [
          "We use your information to prepare and file your tax return, to communicate with you about your engagement, and to verify your identity when you sign in.",
          "We do not use your tax return information for any purpose other than preparing your return without first obtaining your written consent, as required by Internal Revenue Code section 7216.",
        ],
      },
      {
        heading: "Text messages",
        body: [
          "If you choose to sign in by text message, we send a one-time login code to the mobile number you provide. Message frequency varies with how often you sign in. Message and data rates may apply. You can reply STOP at any time to opt out, or HELP for help.",
          "We do not sell, rent, or share mobile phone numbers or text-message consent with third parties for their own marketing purposes. Mobile numbers are shared only with the messaging provider that delivers your login code on our behalf, and only for that purpose.",
          `If you opt out of text messages you can continue to sign in using your email address. For help with text messages, reply HELP, email ${siteConfig.contactEmail}, or call ${siteConfig.contactPhone}.`,
        ],
      },
      {
        heading: "How we share information",
        body: [
          "We share your information only with the service providers that operate this portal on our behalf — hosting, storage, email delivery, and text-message delivery — and only to the extent needed to provide those services. We may also disclose information where required by law.",
          "We do not sell your personal information.",
        ],
      },
      {
        heading: "Artificial intelligence and automated processing",
        body: [
          "We use automated systems and enterprise artificial intelligence services solely to assist with conversational intake, question routing, and document optical character recognition (OCR).",
          "Automated processing is designed to minimize third-party retention and is governed by the provider terms and configuration for the applicable environment. Subedi Tax Pro does not sell tax information or use it to train external AI models; provider retention and model-use controls are disclosed as they apply to each service.",
        ],
      },
      {
        heading: "Data residency & protection",
        body: [
          "Our application, database, document storage, and backup infrastructure are configured in U.S. regions. Some third-party services may process data under their own terms and configuration; this policy identifies the controls we currently apply.",
          "Public connections use HTTPS; documents and account data are encrypted in storage, access is strictly authenticated and audit-logged, and we retain tax records for the periods mandated by federal and California law before secure deletion.",
        ],
      },
      {
        heading: "Your choices",
        body: [
          `You may request a copy of the information we hold about you, ask us to correct it, or ask us to delete information we are not required to retain. Unconverted contact inquiries are deleted after 90 days; client tax records follow the applicable legal retention period. Write to ${siteConfig.contactEmail}.`,
        ],
      },
    ],
  },
  terms: {
    title: "Terms of Service",
    updated: "August 8, 2026",
    sections: [
      {
        heading: "Who provides this service",
        body: [
          `This site and client portal are provided by ${LEGAL_ENTITY}. By using them you agree to these terms.`,
        ],
      },
      {
        heading: "What this service is",
        body: [
          "This portal lets you share tax documents, answer questions about your tax situation, and track the progress of your return. Using the portal does not by itself create a preparer engagement; a separate engagement agreement governs the preparation of your return and its fees.",
          "Nothing on this site is tax, legal, or financial advice until we have agreed to prepare your return.",
        ],
      },
      {
        heading: "Your account",
        body: [
          "You are responsible for keeping access to your email account and mobile number secure, since either can be used to sign in. Tell us promptly if you believe someone else has gained access to your account.",
          "You agree that the information you provide is accurate and that you have the right to share the documents you upload.",
        ],
      },
      {
        heading: "Text messages",
        body: [
          "Signing in by text message is optional. If you opt in, you agree to receive one-time login codes at the number you provide. Message frequency varies with sign-in activity, and message and data rates may apply. Reply STOP to opt out or HELP for help.",
        ],
      },
      {
        heading: "Availability and limits",
        body: [
          "We aim to keep the portal available, but it may be unavailable for maintenance or reasons outside our control. Filing deadlines remain your responsibility, and you should not rely solely on the portal being reachable close to a deadline.",
          "To the extent permitted by law, our liability arising from your use of this site is limited to the fees you have paid us for the engagement to which the claim relates.",
        ],
      },
      {
        heading: "Changes",
        body: [
          "We may update these terms. Material changes will be reflected in the date at the top of this page, and continued use of the portal after a change means you accept the updated terms.",
        ],
      },
      {
        heading: "Governing law",
        body: [
          "These terms are governed by the laws of the State of California.",
        ],
      },
    ],
  },
};
